// L-ASS.0 FRONT-G1: fonte única do texto do contrato de locação.
// Tela (ContractTemplate) e PDF (ContractDocument) renderizam o MESMO texto pelo MESMO parser.
// Texto = modelo ativo da empresa (rental_contract_templates) quando tiver conteúdo; senão, o padrão abaixo.
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatDocument } from '@/lib/utils';
import type { ContractDocumentProps } from '@/components/Documents/Templates/ContractDocument';

export interface ContractBlock {
  type: 'clause' | 'paragraph' | 'item';
  text: string;
}

export interface ResolvedRentalContract {
  title: string;
  content: string;
}

// Transcrição literal das cláusulas que viviam no JSX de ContractTemplate.tsx (sem reescrita).
export const DEFAULT_RENTAL_CONTRACT: ResolvedRentalContract = {
  title: 'CONTRATO DE LOCAÇÃO DE CILINDROS E EQUIPAMENTOS',
  content: `CLÁUSULA PRIMEIRA – DA LOCAÇÃO DE CADA CILINDRO OU EQUIPAMENTO
A locação de cada cilindro ou equipamento ("Itens Locados") será comprovada mediante termo de vistoria detalhado, elaborado na entrega e na devolução, contendo descrição, estado, fotos e assinatura de ambas as partes. O valor mensal da locação será estipulado em comum acordo, não podendo ser superior a 10,1% do valor unitário do item. O Locatário assume integral responsabilidade pelos Itens Locados.

CLÁUSULA SEGUNDA – DO PAGAMENTO
O pagamento da locação poderá ser realizado mensal, trimestral ou semestralmente, conforme acordo entre as partes. A alteração da modalidade de pagamento é permitida mediante aviso prévio de 30 (trinta) dias de antecedência. Os valores serão reajustados anualmente pelo IGP-DI.

CLÁUSULA TERCEIRA – DA INADIMPLÊNCIA
Ocorrendo atraso no pagamento de qualquer parcela da locação, o valor devido será acrescido de multa de 3% (três por cento), juros de mora de 1% (um por cento) ao mês, calculados pro rata die, e correção monetária pelo mesmo índice de reajuste do contrato, até a data do efetivo pagamento.

CLÁUSULA QUARTA – DO VALOR EM CASO DE PERDA OU INFRAÇÃO
Em caso de perda, extravio ou infração contratual, o Locatário pagará o valor do item constante na Nota Fiscal, corrigido pelo IPCA desde o início do contrato ou pelo valor vigente do ano, pagando o menor preço entre as duas modalidades.

CLÁUSULA QUINTA – DAS OBRIGAÇÕES DO LOCATÁRIO
O Locatário se obriga a:
- Utilizar e manter os itens em perfeitas condições de segurança e conservação.
- Permitir que funcionários da Locadora examinem os itens sempre que necessário.
- Não realizar, por si ou por terceiros, qualquer modificação, reparo ou conserto nos itens locados.
- Usar os cilindros exclusivamente para acondicionamento de gases adquiridos da Locadora.
- Não ceder ou transferir os itens a terceiros sob nenhuma hipótese.

CLÁUSULA SEXTA – DO ADITIVO CONTRATUAL
Qualquer alteração na quantidade de itens locados, para mais ou para menos, exigirá a emissão de aditivos contratuais, sendo este o único documento comprobatório para fins de responsabilidade patrimonial e das obrigações aqui mencionadas.

CLÁUSULA SÉTIMA – DA TRANSFERÊNCIA DE LOCAL
A transferência dos Itens Locados para outro endereço somente poderá ocorrer mediante comunicação prévia e por escrito à Locadora, sob pena de multa de 20% sobre o valor do item transferido sem autorização.

CLÁUSULA OITAVA – DA DEVOLUÇÃO E MULTA
A retenção dos itens pelo Locatário por mais de 5 (cinco) dias após o prazo de devolução o obrigará ao pagamento de uma multa diária equivalente a 1% do valor do item, por dia de atraso e por item, até a efetiva devolução.

CLÁUSULA NONA – DA RESCISÃO
O presente contrato tem prazo de vigência indeterminado, podendo ser rescindido por qualquer das partes, a qualquer momento, mediante comunicação por escrito com antecedência mínima de 30 (trinta) dias. A infração de qualquer obrigação, além de causar a rescisão, obrigará o infrator à reparação por perdas e danos.

CLÁUSULA DÉCIMA – DA SUCESSÃO E ALTERAÇÃO SOCIETÁRIA
Este Termo de Responsabilidade continuará em vigor mesmo que qualquer das partes seja objeto de incorporação, fusão ou qualquer alteração societária, obrigando-se a comunicar o fato à outra parte.

CLÁUSULA DÉCIMA PRIMEIRA – DO FORO
As partes elegem o foro da cidade de Colombo, Estado do Paraná, com expressa renúncia de qualquer outro, para dirimir todas as questões oriundas do presente instrumento.

Declara o CLIENTE ter lido, compreendido e estar de pleno acordo com todas as cláusulas e condições do presente contrato. Em sinal de concordância, data e assina este instrumento, obrigando-se por si, seus herdeiros e sucessores.`,
};

const CLAUSE_RE = /^CL[ÁA]USULA\b/i;
const ITEM_RE = /^(- |• )/;

/**
 * Converte o texto puro do contrato em blocos tipados.
 * Linha vazia é ignorada; "CLÁUSULA ..." => clause; "- " ou "• " no início => item (sem o marcador);
 * qualquer outra linha => paragraph.
 */
export function parseContractContent(content: string): ContractBlock[] {
  const blocks: ContractBlock[] = [];
  for (const rawLine of (content ?? '').split('\n')) {
    const line = rawLine.trim();
    if (!line) continue;
    if (CLAUSE_RE.test(line)) {
      blocks.push({ type: 'clause', text: line });
    } else if (ITEM_RE.test(line)) {
      blocks.push({ type: 'item', text: line.replace(ITEM_RE, '').trim() });
    } else {
      blocks.push({ type: 'paragraph', text: line });
    }
  }
  return blocks;
}

export type ClausePart = { kind: 'p'; text: string } | { kind: 'list'; items: string[] };
export interface ClauseGroup { title?: string; parts: ClausePart[] }

/**
 * Agrupa os blocos do parser por cláusula: título + parágrafos + listas (itens consecutivos).
 * Compartilhado por tela (ContractTemplate) e PDF (ContractDocument).
 */
export function groupContractBlocks(blocks: ContractBlock[]): ClauseGroup[] {
  const groups: ClauseGroup[] = [];
  let current: ClauseGroup | null = null;
  const ensure = () => { if (!current) current = { parts: [] }; return current; };
  for (const block of blocks) {
    if (block.type === 'clause') {
      if (current) groups.push(current);
      current = { title: block.text, parts: [] };
    } else if (block.type === 'item') {
      const g = ensure();
      const last = g.parts[g.parts.length - 1];
      if (last && last.kind === 'list') last.items.push(block.text);
      else g.parts.push({ kind: 'list', items: [block.text] });
    } else {
      ensure().parts.push({ kind: 'p', text: block.text });
    }
  }
  if (current) groups.push(current);
  return groups;
}

/**
 * Devolve o texto do modelo da empresa quando existir e tiver conteúdo; senão o contrato padrão.
 */
export function resolveRentalContract(
  template?: { title?: string | null; content?: string | null } | null,
): ResolvedRentalContract {
  const content = template?.content ?? '';
  if (content.trim().length === 0) return DEFAULT_RENTAL_CONTRACT;
  return {
    title: template?.title?.trim() ? template.title : DEFAULT_RENTAL_CONTRACT.title,
    content,
  };
}

/**
 * m-ASS.7: mensagem amigável para erros das RPCs de assinatura (request/cancel).
 * Códigos conhecidos viram frase; demais: a parte depois de ": " quando houver, senão a mensagem inteira.
 */
export function friendlyContractError(message: string): string {
  const msg = message ?? '';
  if (msg.includes('insufficient_privilege')) return 'Você não tem permissão para gerir contratos de locação.';
  if (msg.includes('Acesso negado')) return 'Sessão expirada ou sem acesso. Entre novamente.';
  if (msg.includes('rental_not_found')) return 'Locação não encontrada.';
  if (msg.includes('signature_not_found')) return 'Envio não encontrado.';
  // L-ASS.3 FRONT-J1: entrega de locação (schedule/cancel_rental_delivery)
  if (msg.includes('delivery_already_scheduled')) return 'Esta locação já tem entrega aberta na logística.';
  if (msg.includes('rental_not_active')) return 'Só locação ativa pode ser enviada para entrega.';
  if (msg.includes('delivery_method_invalid')) return 'Método de entrega inválido.';
  if (msg.includes('no_open_delivery')) return 'Esta locação não tem entrega aberta.';
  if (msg.includes('delivery_in_transit')) return 'A entrega está em trânsito: o responsável cancela pelo app, ou devolve ao pool antes.';
  if (msg.includes('reason_required') && msg.indexOf(': ') < 0) return 'Informe o motivo.';
  // L-ASS.3 FRONT-J2: confirmação presencial da entrega (confirm_rental_delivery_with_signature)
  if (msg.includes('contract_not_available')) return 'Contrato alterado ou não enviado. Peça ao escritório para enviar para entrega de novo.';
  if (msg.includes('delivery_already_confirmed')) return 'Esta entrega já foi confirmada ou cancelada.';
  if (msg.includes('permission_denied')) return 'Sem permissão para confirmar entregas.';
  if (msg.includes('delivery_not_found')) return 'Entrega não encontrada.';
  const idx = msg.indexOf(': ');
  return idx >= 0 ? msg.slice(idx + 2) : msg;
}

/**
 * L-ASS.1 FRONT-H1: o que fica congelado no envio para assinatura — título, texto e o
 * documentData da tela (summary), sem transformação. É o que request_contract_signature grava e hasheia.
 */
export interface ContractSnapshot {
  title: string;
  content: string;
  summary: Record<string, unknown>;
}

/**
 * Props do PDF do contrato (ContractDocument) a partir do documentData da tela (ContractTemplate).
 * Função pura: mesmos dados e mesmos formatos nos dois lados. rentalNumber = data.id, customerName = data.client.
 */
export function buildContractPdfProps(
  data: any,
  contract: { title: string; content: string },
): ContractDocumentProps {
  const fmtDate = (d?: string | null) => {
    if (!d) return undefined;
    try { return format(new Date(d.substring(0, 10) + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR }); } catch { return undefined; }
  };
  const lessorName: string = data.company?.name || data.company?.razao_social || 'EMPRESA PRINCIPAL';
  const companyAddress = [
    data.company?.endereco, data.company?.numero, data.company?.bairro,
    data.company?.cidade, data.company?.estado, data.company?.cep,
  ].filter(Boolean).join(', ') || undefined;
  const customerAddress = [
    data.customer?.address, data.customer?.city,
    data.customer?.state, data.customer?.zip_code,
  ].filter(Boolean).join(', ') || undefined;
  const docType: 'cpf' | 'cnpj' | undefined = data.customer?.document_type;
  return {
    title: contract.title,
    content: contract.content,
    companyName: lessorName,
    companyCnpj: data.company?.cnpj ? formatDocument(data.company.cnpj, 'cnpj') : undefined,
    companyAddress,
    logoUrl: data.company?.logo_url,
    rentalNumber: data.id,
    customerName: data.client,
    contractDate: format(new Date(), "dd 'de' MMMM 'de' yyyy", { locale: ptBR }),
    lessor: {
      name: lessorName.toUpperCase(),
      documentLabel: 'CNPJ',
      document: data.company?.cnpj ? formatDocument(data.company.cnpj, 'cnpj') : undefined,
      address: companyAddress,
    },
    lessee: {
      name: String(data.customer?.name || data.client || '').toUpperCase(),
      documentLabel: docType ? (docType === 'cpf' ? 'CPF' : 'CNPJ') : undefined,
      document: docType && data.customer?.document_number
        ? formatDocument(data.customer.document_number, docType)
        : undefined,
      address: customerAddress,
    },
    summary: {
      items: (data.equipments ?? []).map((eq: { type: string; gas?: string; qty: number; unitValue?: number; serialNumber?: string }) => ({
        description: eq.gas ? `${eq.type} (${eq.gas})` : eq.type,
        quantity: eq.qty,
        serialNumber: eq.serialNumber,
        unitValue: eq.unitValue,
      })),
      monthlyValue: data.monthlyValue,
      periodLabel: data.periodLabel,
      startDate: fmtDate(data.startDate),
      nextDueDate: fmtDate(data.nextDueDate),
    },
  };
}
